import { PopoverButton } from '@headlessui/react';
import { Link, useFetcher } from '@remix-run/react';
import { GQLCartItemFragment, GQLOperationalSettingsFragment } from '@theprintbar/graphql';
import {
	DesignParamAction,
	WebRoutes,
	capitalise,
	cn,
	getCurrencyString,
	getDecorationBGColour,
	getSamedayName,
	getVariantColour,
} from '@theprintbar/shared';
import { LightningBolt, MerchPreview, Pill, gaCustomEvent } from '@theprintbar/ui';
import { useEffect } from 'react';
import { getCartItemCost } from '~/utils';

interface CartItemProps {
	sameday?: boolean;
	settings: GQLOperationalSettingsFragment;
	item: GQLCartItemFragment;
	hasPopover?: boolean;
	hideLinks?: boolean;
	quoteSession?: any;
}

export function CartItem({ settings, hasPopover, sameday, item, quoteSession, hideLinks }: CartItemProps) {
	const fetcher = useFetcher<{ reload?: boolean }>();

	const { merch, lines } = item;

	const { product, decorations } = merch;

	const variant = lines?.[0]?.variant;
	const colour = merch.product?.colours?.find(({ id }) => id === variant?.colour.id);
	if (!product || !variant || !colour) return null;

	const { quantity, total } = getCartItemCost(settings, item, sameday);

	const singlePrice = total / quantity;

	useEffect(() => {
		if (fetcher.state === 'idle' && fetcher.data?.reload) {
			console.log('Cart item removed, refreshing cart');
			window.location.reload();
		}
	}, [fetcher]);

	const handleItemRemove = () => {
		fetcher.submit(
			{ cartItemId: item.id },
			{
				method: 'POST',
				action: (quoteSession ? WebRoutes.API.commerce.quote : WebRoutes.API.commerce.cart).remove,
			},
		);

		if (!quoteSession) {
			gaCustomEvent({
				action: 'remove_from_cart',
				params: {
					currency: 'AUD',
					value: total,
				},
				items: [
					{
						id: product,
						name: product.name,
						quantity,
						total: total,
					},
				],
			});
		}
	};

	return (
		<div className="flex w-full py-2 lg:py-4 gap-4 lg:gap-8 pr-1">
			<div className="relative my-auto">
				<MerchPreview allowPreview merch={merch} colourCode={getVariantColour(product, variant)?.code} />
				<div className="absolute -top-2 -right-2 bg-yellow border border-blue rounded-full w-6 h-6 flex items-center justify-center text-blue text-xs font-bold">
					{quantity}
				</div>
			</div>

			<div className="w-full">
				<div className="flex justify-between w-full">
					<div>
						<h5 className="text-xs uppercase">{product.brand?.name}</h5>
						{sameday && (
							<Pill className="py-0.5 px-1 flex items-center bg-blue-bright">
								<LightningBolt className="w-3 h-3 lg:w-4 lg:h-4" />
								{getSamedayName()}
							</Pill>
						)}
					</div>
					<div className="text-right">
						<p className="font-normal text-xs lg:text-sm">{getCurrencyString(total)}</p>
						{quantity > 1 && (
							<p className="font-normal text-xs lg:text-sm text-grey">
								<small>{getCurrencyString(singlePrice)} ea</small>
							</p>
						)}
					</div>
				</div>
				<p className="font-display font-light text-sm lg:text-base max-w-sm overflow-ellipsis">
					{hideLinks ? product.name : <Link to={WebRoutes.Product(product.slug || '')}>{product.name}</Link>}
				</p>
				{merch.name && merch.name !== 'Untitled Merch' && (
					<div>
						<p className="text-xs">
							<b>Name:</b> {merch.name}
						</p>
					</div>
				)}
				<div className="mb-2">
					<h5 className="text-xs uppercase mt-1 lg:mt-2">{colour.colour.name}</h5>
					<div className="mt-1 flex flex-wrap gap-1">
						{item.lines?.map((line, index) => (
							<div className="px-1.5 py-0.5 text-[10px] lg:text-xs bg-blue text-white rounded-full" key={line.id}>
								{line.quantity} x {line.variant.size.code}
							</div>
						))}
						{decorations?.map((decoration, index) => {
							const bgColour = getDecorationBGColour(decoration.type);
							return (
								<div
									className={cn(
										'px-1.5 py-0.5 text-[10px] lg:text-xs bg-opacity-40 text-blue border border-blue rounded-full',
										bgColour,
									)}
									key={decoration.id}
									style={{ backgroundColor: bgColour }}
								>
									{capitalise(decoration.side?.side)}
									{!!decoration.requestedColours && ` - ${decoration.requestedColours} colours`}
								</div>
							);
						})}
					</div>
				</div>
				{!hideLinks && (
					<div className="flex justify-between w-full items-end">
						{hasPopover ? (
							<PopoverButton
								as={Link}
								className="font-normal text-xs underline text-grey"
								reloadDocument
								to={WebRoutes.Products({ ref: item.id, action: DesignParamAction.Duplicate, sameday })}
							>
								Reuse this design
							</PopoverButton>
						) : (
							<Link
								className="font-normal text-xs underline text-grey"
								to={WebRoutes.Products({ ref: item.id, action: DesignParamAction.Duplicate, sameday })}
							>
								Reuse this design
							</Link>
						)}

						<div className="space-x-5 flex items-center">
							{hasPopover ? (
								<PopoverButton
									as={Link}
									className="font-normal text-xs underline text-grey"
									reloadDocument
									to={WebRoutes.Product(product.slug, { ref: item.id, action: DesignParamAction.Edit, sameday })}
								>
									Edit
								</PopoverButton>
							) : (
								<Link
									className="font-normal text-xs underline text-grey"
									reloadDocument
									to={WebRoutes.Product(product.slug, { ref: item.id, action: DesignParamAction.Edit, sameday })}
								>
									Edit
								</Link>
							)}
							<button className="font-normal text-xs underline text-grey" onClick={handleItemRemove}>
								Remove
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
